import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Form, Modal, Input, InputNumber, Button, Space } from 'antd';
import ReactQuill, { Quill } from 'react-quill';
import axios from 'axios';
import { InputInterface } from '../utils/forms/FormInterface';
import { BASE_API_URL, BASE_IMAGE_URL } from '../env';
import { getToken } from '../service/CookieManager';
import { toastError, toastSuccess } from '../service/Toast';

import 'react-quill/dist/quill.snow.css';

// Register custom table embed blot for ReactQuill if not already registered
const BlockEmbed = Quill.import('blots/block/embed') as any;

class NewsTableEmbed extends BlockEmbed {
  static create(value: string) {
    const node = super.create();
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('data-table-embed', 'true');
    node.setAttribute('title', 'Double-click to edit table');
    node.innerHTML = typeof value === 'string' ? value : '';
    return node;
  }

  static value(node: HTMLElement) {
    return node.innerHTML;
  }
}

NewsTableEmbed.blotName = 'newsTableEmbed';
NewsTableEmbed.tagName = 'div';
NewsTableEmbed.className = 'ql-news-table-embed';

if (!(Quill as any).__newsTableEmbedRegistered) {
  Quill.register(NewsTableEmbed);
  (Quill as any).__newsTableEmbedRegistered = true;
}

function escapeHtml(value: string) {
  return (value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function createEmptyGrid(rows: number, cols: number): string[][] {
  return Array.from({ length: Math.max(1, rows) }, () =>
    Array.from({ length: Math.max(1, cols) }, () => '')
  );
}

function parseTableHtml(html: string): string[][] {
  try {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const table = wrapper.querySelector('table');
    if (!table) return createEmptyGrid(3, 3);

    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) return createEmptyGrid(3, 3);

    return rows.map((row) =>
      Array.from(row.querySelectorAll('th,td')).map((cell) =>
        (cell.textContent || '').trim()
      )
    );
  } catch {
    return createEmptyGrid(3, 3);
  }
}

function buildTableHtml(grid: string[][]): string {
  if (!grid.length) return '';

  const header = grid[0] || [];
  const bodyRows = grid.slice(1);

  const headerHtml = `<thead style="background:linear-gradient(to right, #004195, #0056b3);color:#fff;"><tr>${header
    .map(
      (cell) =>
        `<th style="border:1px solid #cbd5e1;padding:10px 14px;color:#ffffff;font-weight:600;text-align:left;">${escapeHtml(
          cell || 'Header'
        )}</th>`
    )
    .join('')}</tr></thead>`;

  const bodyHtml = `<tbody>${bodyRows
    .map(
      (row) =>
        `<tr>${row
          .map(
            (cell) =>
              `<td style="border:1px solid #e2e8f0;padding:10px 14px;color:#1e293b;">${escapeHtml(
                cell || ''
              )}</td>`
          )
          .join('')}</tr>`
    )
    .join('')}</tbody>`;

  return `<table style="width:100%;border-collapse:collapse;margin:8px 0;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">${headerHtml}${bodyHtml}</table>`;
}

interface TextEditorProps {
  formInput: InputInterface;
  index: number;
}

const TextEditor = (props: TextEditorProps) => {
  const { formInput, index } = props;
  const quillRef = useRef<ReactQuill | null>(null);

  // Table Modal States
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableGrid, setTableGrid] = useState<string[][]>(() => createEmptyGrid(3, 3));
  const [editingTableIndex, setEditingTableIndex] = useState<number | null>(null);

  // Image Modal States
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const openNewTableModal = () => {
    setEditingTableIndex(null);
    setTableRows(3);
    setTableCols(3);
    setTableGrid(createEmptyGrid(3, 3));
    setTableModalOpen(true);
  };

  const openEditTableModal = (embedIndex: number, html: string) => {
    const grid = parseTableHtml(html);
    setEditingTableIndex(embedIndex);
    setTableRows(grid.length);
    setTableCols(Math.max(...grid.map((row) => row.length), 1));
    setTableGrid(grid);
    setTableModalOpen(true);
  };

  // Double-click handler to edit existing table embeds
  useEffect(() => {
    let root: HTMLElement | null = null;

    const onDoubleClick = (event: MouseEvent) => {
      const editor = quillRef.current?.getEditor();
      const target = event.target as HTMLElement | null;
      const tableNode = target?.closest?.('.ql-news-table-embed') as HTMLElement | null;
      if (!tableNode || !editor) return;

      const blot = (Quill as any).find(tableNode);
      if (!blot) return;

      const embedIndex = editor.getIndex(blot);
      openEditTableModal(embedIndex, tableNode.innerHTML);
    };

    const timer = setTimeout(() => {
      root = quillRef.current?.getEditor()?.root || null;
      root?.addEventListener('dblclick', onDoubleClick);
    }, 200);

    return () => {
      clearTimeout(timer);
      root?.removeEventListener('dblclick', onDoubleClick);
    };
  }, []);

  const resizeGrid = (rows: number, cols: number) => {
    setTableRows(rows);
    setTableCols(cols);
    setTableGrid((prev) =>
      Array.from({ length: rows }, (_, r) =>
        Array.from({ length: cols }, (_, c) => prev[r]?.[c] || '')
      )
    );
  };

  const insertOrUpdateTable = () => {
    const html = buildTableHtml(tableGrid);
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    if (editingTableIndex !== null) {
      editor.deleteText(editingTableIndex, 1);
      editor.insertEmbed(editingTableIndex, 'newsTableEmbed', html);
      editor.setSelection(editingTableIndex + 1, 0);
    } else {
      const range = editor.getSelection(true);
      const insertPos = range ? range.index : editor.getLength();
      editor.insertEmbed(insertPos, 'newsTableEmbed', html);
      // Keep cursor after embed without forcing an extra blank paragraph gap
      editor.setSelection(insertPos + 1, 0);
    }

    setTableModalOpen(false);
    toastSuccess('Table inserted into article');
  };

  const handleImageUploadFile = async (file: File) => {
    const token = getToken(`${BASE_API_URL}/news/upload`) || getToken();
    const formData = new FormData();
    formData.append('image', file);

    setUploadingImage(true);
    try {
      const uploadUrl = `${BASE_API_URL}/news/upload`;
      const res = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const uploadedPath = res.data?.data;
      let finalUrl = uploadedPath;
      if (uploadedPath && !uploadedPath.startsWith('http')) {
        const base = (BASE_IMAGE_URL || '').replace(/\/+$/, '');
        finalUrl = `${base}/${uploadedPath.replace(/^\/+/, '')}`;
      }

      insertImageToEditor(finalUrl, file.name);
      setImageModalOpen(false);
      toastSuccess('Image uploaded and inserted');
    } catch (err) {
      toastError('Failed to upload image. You can also paste an image URL directly.');
    } finally {
      setUploadingImage(false);
    }
  };

  const insertImageToEditor = (url: string, altText?: string) => {
    if (!url) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const insertPos = range ? range.index : editor.getLength();
    editor.insertEmbed(insertPos, 'image', url);
    // Avoid inserting blank <p><br></p> that creates large empty space on the frontend
    editor.setSelection(insertPos + 1, 0);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ size: ['small', false, 'large', 'huge'] }, { color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link', 'image', 'newsTable', 'code-block'],
          ['clean'],
        ],
        handlers: {
          newsTable: () => {
            openNewTableModal();
          },
          image: () => {
            setImageUrl('');
            setImageAlt('');
            setImageModalOpen(true);
          },
        },
      },
      clipboard: { matchVisual: false },
    }),
    []
  );

const QuillField = React.forwardRef(({ value, onChange, modules, ...rest }: any, ref: any) => (
  <div className="custom-quill-editor-wrapper">
    <ReactQuill ref={ref} theme="snow" modules={modules} value={value || ''} onChange={onChange} {...rest} />
    <div className="editor-helper-bar mt-1 text-xs text-gray-500 flex justify-between">
      <span>Tip: Use Heading 2 for automatic Table of Contents items. Use Table icon for comparison tables.</span>
    </div>
  </div>
));

  return (
    <>
      <Form.Item
        key={index}
        wrapperCol={{ span: 8, offset: 4 }}
        labelCol={{ span: 4 }}
        label={<div className={!formInput.required ? 'leftOffset' : ''}>{formInput.label}</div>}
        colon={formInput.required}
        labelAlign="left"
        name={formInput.name}
        rules={[
          {
            required: formInput.required,
            message: formInput.errorMsg,
          },
        ]}
      >
        <QuillField ref={quillRef} modules={modules} />
      </Form.Item>

      {/* Table Insertion / Editing Modal */}
      <Modal
        title={editingTableIndex !== null ? 'Edit Data Table' : 'Insert Data Table'}
        open={tableModalOpen}
        onOk={insertOrUpdateTable}
        onCancel={() => setTableModalOpen(false)}
        width={750}
        okText={editingTableIndex !== null ? 'Update Table' : 'Insert Table'}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space>
            <span>Rows:</span>
            <InputNumber
              min={1}
              max={20}
              value={tableRows}
              onChange={(val) => resizeGrid(Number(val) || 1, tableCols)}
            />
            <span>Columns:</span>
            <InputNumber
              min={1}
              max={10}
              value={tableCols}
              onChange={(val) => resizeGrid(tableRows, Number(val) || 1)}
            />
            <span style={{ fontSize: 12, color: '#64748b' }}>
              (Row 1 will be styled as the table header)
            </span>
          </Space>

          <div
            style={{
              maxHeight: 350,
              overflow: 'auto',
              border: '1px solid #e2e8f0',
              padding: 12,
              borderRadius: 6,
              background: '#f8fafc',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {tableGrid.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} style={{ padding: 4 }}>
                        <Input
                          placeholder={rIdx === 0 ? `Header ${cIdx + 1}` : `Row ${rIdx} Col ${cIdx + 1}`}
                          style={
                            rIdx === 0
                              ? { fontWeight: 600, background: '#e0f2fe', borderColor: '#0284c7' }
                              : undefined
                          }
                          value={cell}
                          onChange={(e) => {
                            const val = e.target.value;
                            setTableGrid((prev) => {
                              const next = prev.map((r) => [...r]);
                              next[rIdx][cIdx] = val;
                              return next;
                            });
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Space>
      </Modal>

      {/* Image Insertion Modal */}
      <Modal
        title="Insert Image into Article"
        open={imageModalOpen}
        onCancel={() => setImageModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setImageModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="insert"
            type="primary"
            disabled={!imageUrl.trim()}
            onClick={() => {
              insertImageToEditor(imageUrl.trim(), imageAlt.trim());
              setImageModalOpen(false);
            }}
          >
            Insert by URL
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Upload from computer:</p>
            <input
              type="file"
              accept="image/*"
              disabled={uploadingImage}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImageUploadFile(file);
                }
              }}
            />
            {uploadingImage && <p style={{ color: '#004195', marginTop: 4 }}>Uploading image to S3...</p>}
          </div>

          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Or enter image URL:</p>
            <Input
              placeholder="https://example.com/image.png or /static-images/..."
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
            <Input
              placeholder="Image Alt description (optional)"
              style={{ marginTop: 8 }}
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default TextEditor;
